import type { CompositePrimaryKey, FieldFilter } from '#layers/data-model/public';
import { isNonArrayRecord } from '#layers/base/public';
import { CURRENT_USER_VARIABLE, FilterOperator, LogicalOperator, UnaryFilterOperator } from '#layers/data-model/public';

/**
 * The four operators a related-item clause offers, mirroring the ID-style filter: identity
 * (`_eq` / `_in`) and their exclusions (`_neq` / `_nin`).
 */
export type RelatedItemOperator =
	| typeof FilterOperator.EQ
	| typeof FilterOperator.NEQ
	| typeof FilterOperator.IN
	| typeof FilterOperator.NIN;

/**
 * Where a picked key's value lands in the terminal match: the key carries it under `key` (a field of the
 * picked collection), the match compares it on `column`. The two differ when the match is written on
 * the foreign key columns of the collection holding the relation.
 */
export interface RelatedItemKeyColumn {
	key: string;
	column: string;
	/**
	 * Whether the column can hold NULL in the rows the match reads. A traversed column cannot: the
	 * relation only reaches rows its key joins. An exclusion on a nullable column also requires it to
	 * be non-null, so it matches only rows that reference an item, as a traversal would.
	 */
	isNullable: boolean;
}

/** The operator + selected item key(s) a clause's terminal match resolves to. */
export interface RelatedItemTerminalMatch {
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
}

export function isExclusionOperator(operator: RelatedItemOperator): boolean {
	return operator === FilterOperator.NEQ || operator === FilterOperator.NIN;
}

export function negateOperator(operator: RelatedItemOperator): RelatedItemOperator {
	switch (operator) {
		case FilterOperator.EQ: return FilterOperator.NEQ;
		case FilterOperator.NEQ: return FilterOperator.EQ;
		case FilterOperator.IN: return FilterOperator.NIN;
		case FilterOperator.NIN: return FilterOperator.IN;
		default: return operator;
	}
}

function toPositiveOperator(operator: RelatedItemOperator): RelatedItemOperator {
	if (operator === FilterOperator.NEQ) return FilterOperator.EQ;
	if (operator === FilterOperator.NIN) return FilterOperator.IN;
	return operator;
}

/**
 * Reads the terminal match — the value of the innermost relation hop — into an operator + key(s).
 * The shape encodes the operator (see the table in `buildTerminalMatch`). Returns `null` when the
 * shape is not a related-item identity/null match (e.g. a range operator on the key), so the row
 * falls through to the generic primitive editor.
 */
export function parseTerminalMatch(value: unknown, columns: RelatedItemKeyColumn[]): RelatedItemTerminalMatch | null {
	if (!isNonArrayRecord(value)) return null;
	if (!isCompositeExclusion(value)) return parseIdentityMatch(value, columns);

	// A composite exclusion is the negated variant of the operator its `_not` wraps.
	const negated = compositeExclusionOperand(value, columns);
	const inner = negated === undefined ? null : parseIdentityMatch(negated, columns);
	if (!inner || isExclusionOperator(inner.operator)) return null;
	return { operator: negateOperator(inner.operator), keys: inner.keys };
}

/** Whether `value` is shaped as a composite exclusion: a `_not`, alone or leading an `_and`. */
function isCompositeExclusion(value: Record<string, unknown>): boolean {
	if (UnaryFilterOperator.NOT in value) return true;
	const terms = value[LogicalOperator.AND];
	return Array.isArray(terms) && isNonArrayRecord(terms[0]) && UnaryFilterOperator.NOT in terms[0];
}

/**
 * The positive match a composite exclusion negates, when it guards exactly the nullable columns:
 * `{ _not: <positive> }` without nullable columns, `{ _and: [{ _not: <positive> }, { col: { _null: false } }, …] }`
 * with them. `undefined` for any other shape.
 */
function compositeExclusionOperand(value: Record<string, unknown>, columns: RelatedItemKeyColumn[]): unknown {
	if (Object.keys(value).length !== 1) return undefined;
	const nullable = columns.filter((column) => column.isNullable);
	if (nullable.length === 0) return value[UnaryFilterOperator.NOT];

	const terms = value[LogicalOperator.AND];
	if (!Array.isArray(terms)) return undefined;
	const [exclusion, ...guards] = terms;
	if (!isNonArrayRecord(exclusion) || Object.keys(exclusion).length !== 1) return undefined;
	return isNullGuardFor(guards, nullable) ? exclusion[UnaryFilterOperator.NOT] : undefined;
}

/** Whether `guards` requires exactly the `nullable` columns to be non-null, one `{ col: { _null: false } }` each. */
function isNullGuardFor(guards: unknown[], nullable: RelatedItemKeyColumn[]): boolean {
	if (guards.length !== nullable.length) return false;

	return nullable.every(({ column }) => guards.some((guard) => isNonArrayRecord(guard) && Object.keys(guard).length === 1 && isNullGuard(guard[column])));
}

function isNullGuard(value: unknown): boolean {
	return isNonArrayRecord(value) && Object.keys(value).length === 1 && value[FilterOperator.NULL] === false;
}

function parseIdentityMatch(value: unknown, columns: RelatedItemKeyColumn[]): RelatedItemTerminalMatch | null {
	if (!isNonArrayRecord(value)) return null;

	const [single, ...rest] = columns;
	if (!single) return null;
	if (rest.length === 0) return parseSingleColumnMatch(value, single);

	// Composite "one of": `{ _or: [ <key>, … ] }`.
	if (Object.keys(value).length === 1 && Array.isArray(value._or)) {
		const keys: CompositePrimaryKey[] = [];

		for (const entry of value._or) {
			const key = parseCompositeKey(entry, columns);
			if (!key) return null;
			keys.push(key);
		}

		return keys.length > 0 ? { operator: FilterOperator.IN, keys } : null;
	}

	// Composite "is": a single `{ colA: { _eq }, colB: { _eq } }` conjunction.
	const key = parseCompositeKey(value, columns);
	return key ? { operator: FilterOperator.EQ, keys: [key] } : null;
}

function parseSingleColumnMatch(value: Record<string, unknown>, column: RelatedItemKeyColumn): RelatedItemTerminalMatch | null {
	// The `$CURRENT_USER` split emitted by `buildMembershipMatch`: `_or` of `_eq` + `_in` (membership),
	// `_and` of `_neq` + `_nin` (exclusion). Recombine the branch keys into one IN/NIN terminal.
	if (Array.isArray(value._or)) return parseSplitMembership(value._or, FilterOperator.IN, column);
	if (Array.isArray(value._and)) return parseSplitMembership(value._and, FilterOperator.NIN, column);

	const inner = value[column.column];
	if (Object.keys(value).length !== 1 || !isNonArrayRecord(inner)) return null;

	const { [FilterOperator.NULL]: nullGuard, ...comparison } = inner;
	const [entry, ...rest] = Object.entries(comparison);
	if (!entry || rest.length > 0) return null;

	const match = parseComparison(entry[0], entry[1], column.key);
	if (!match) return null;

	const isGuarded = FilterOperator.NULL in inner;
	if (isGuarded && nullGuard !== false) return null;
	return isGuarded === requiresNullGuard(match.operator, column) ? match : null;
}

function parseComparison(operator: string, operand: unknown, keyName: string): RelatedItemTerminalMatch | null {
	switch (operator) {
		case FilterOperator.EQ: return { operator: FilterOperator.EQ, keys: toSingleColumnKeys([operand], keyName) };
		case FilterOperator.NEQ: return { operator: FilterOperator.NEQ, keys: toSingleColumnKeys([operand], keyName) };
		case FilterOperator.IN: return parseMembershipMatch(FilterOperator.IN, operand, keyName);
		case FilterOperator.NIN: return parseMembershipMatch(FilterOperator.NIN, operand, keyName);
		// Any other operator (e.g. `_lt`) is a plain primitive filter on the key, not an identity match.
		default: return null;
	}
}

function requiresNullGuard(operator: RelatedItemOperator, column: RelatedItemKeyColumn): boolean {
	return column.isNullable && isExclusionOperator(operator);
}

/**
 * Only the split `buildMembershipMatch` emits: several branches, one of them comparing
 * `$CURRENT_USER`. Any other group over the column is the user's own and stays a group.
 */
function parseSplitMembership(branches: unknown[], operator: typeof FilterOperator.IN | typeof FilterOperator.NIN, column: RelatedItemKeyColumn): RelatedItemTerminalMatch | null {
	if (branches.length < 2) return null;

	const keys: CompositePrimaryKey[] = [];
	const expectExclusion = operator === FilterOperator.NIN;
	let comparesCurrentUser = false;

	for (const branch of branches) {
		if (!isNonArrayRecord(branch)) return null;
		const inner = parseSingleColumnMatch(branch, column);
		if (!inner || isExclusionOperator(inner.operator) !== expectExclusion) return null;
		comparesCurrentUser ||= isCurrentUserComparison(inner, column);
		keys.push(...inner.keys);
	}

	return comparesCurrentUser ? { operator, keys } : null;
}

function isCurrentUserComparison(match: RelatedItemTerminalMatch, column: RelatedItemKeyColumn): boolean {
	const isSingle = match.operator === FilterOperator.EQ || match.operator === FilterOperator.NEQ;
	return isSingle && match.keys[0]?.[column.key] === CURRENT_USER_VARIABLE;
}

function parseMembershipMatch(operator: typeof FilterOperator.IN | typeof FilterOperator.NIN, operand: unknown, keyName: string): RelatedItemTerminalMatch | null {
	if (!Array.isArray(operand)) return null;

	const keys = toSingleColumnKeys(operand, keyName);
	return keys.length > 0 ? { operator, keys } : null;
}

function parseCompositeKey(value: unknown, columns: RelatedItemKeyColumn[]): CompositePrimaryKey | null {
	if (!isNonArrayRecord(value) || Object.keys(value).length !== columns.length) return null;

	const key: CompositePrimaryKey = {};

	for (const { key: keyName, column } of columns) {
		const inner = value[column];
		if (!isNonArrayRecord(inner) || Object.keys(inner).length !== 1 || !('_eq' in inner)) return null;
		key[keyName] = inner._eq as CompositePrimaryKey[string];
	}

	return key;
}

function toSingleColumnKeys(values: unknown[], keyName: string): CompositePrimaryKey[] {
	return values
		.filter((value) => value != null)
		.map((value) => ({ [keyName]: value as CompositePrimaryKey[string] }));
}

/**
 * Serializes an operator + key(s) into the terminal match (the value of the innermost relation hop):
 *
 * | operator      | single column          | composite                     |
 * | ------------- | ---------------------- | ----------------------------- |
 * | is            | `{ id: { _eq } }`      | `{ colA: { _eq }, … }`        |
 * | is one of     | `{ id: { _in } }`      | `{ _or: [ <key>, … ] }`       |
 * | is not        | `{ id: { _neq } }`     | `{ _not: { colA: { _eq } } }` |
 * | isn't one of  | `{ id: { _nin } }`     | `{ _not: { _or: [ … ] } }`    |
 *
 * An exclusion also requires each nullable column to be non-null: `{ id: { _neq, _null: false } }`,
 * `{ _and: [{ _not: … }, { colA: { _null: false } }] }`. The composite guards form an explicit
 * conjunction: every filter walker rebuilds a `_not` alone, and would drop keys beside it.
 */
export function buildTerminalMatch(
	operator: RelatedItemOperator,
	keys: CompositePrimaryKey[],
	columns: RelatedItemKeyColumn[],
	options: { positive?: boolean } = {},
): FieldFilter {
	const [first] = keys;
	const [single, ...rest] = columns;
	if (!first || !single) return {};

	const effective = options.positive ? toPositiveOperator(operator) : operator;

	if (rest.length === 0) {
		const values = keys.map((key) => key[single.key]).filter((value) => value != null);

		switch (effective) {
			case FilterOperator.EQ: return buildComparison(single, FilterOperator.EQ, values[0] ?? null);
			case FilterOperator.NEQ: return buildComparison(single, FilterOperator.NEQ, values[0] ?? null);
			case FilterOperator.IN: return buildMembershipMatch(single, values, false);
			case FilterOperator.NIN: return buildMembershipMatch(single, values, true);
		}
	}

	const isSingle = effective === FilterOperator.EQ || effective === FilterOperator.NEQ;

	// A single "is" match is a bare conjunction (`{ colA: {_eq}, colB: {_eq} }`); a list "is one of" is
	// an `_or` of those conjunctions.
	const positive: FieldFilter = isSingle
		? buildCompositeKeyMatch(first, columns)
		: { _or: keys.map((key) => buildCompositeKeyMatch(key, columns)) };

	if (!isExclusionOperator(effective)) return positive;

	const exclusion: FieldFilter = { [UnaryFilterOperator.NOT]: positive };
	const guards = columns.filter((column) => column.isNullable).map(({ column }): FieldFilter => ({ [column]: { [FilterOperator.NULL]: false } }));
	return guards.length === 0 ? exclusion : { [LogicalOperator.AND]: [exclusion, ...guards] };
}

function buildComparison(column: RelatedItemKeyColumn, operator: RelatedItemOperator, operand: unknown): FieldFilter {
	const guard = requiresNullGuard(operator, column) ? { [FilterOperator.NULL]: false } : {};
	return { [column.column]: { [operator]: operand, ...guard } };
}

/**
 * Serializes a single-column membership (`_in`) or exclusion (`_nin`). `$CURRENT_USER` is a
 * single-value variable the engine only substitutes for `_eq`/`_neq`.
 */
function buildMembershipMatch(column: RelatedItemKeyColumn, values: unknown[], exclude: boolean): FieldFilter {
	const listOp = exclude ? FilterOperator.NIN : FilterOperator.IN;
	const variables = values.filter((value) => value === CURRENT_USER_VARIABLE);
	const concrete = values.filter((value) => value !== CURRENT_USER_VARIABLE);

	if (variables.length === 0) return buildComparison(column, listOp, concrete);

	const eqOp = exclude ? FilterOperator.NEQ : FilterOperator.EQ;
	const clauses: FieldFilter[] = variables.map((variable) => buildComparison(column, eqOp, variable));
	if (concrete.length > 0) clauses.push(buildComparison(column, listOp, concrete));

	const [only, ...others] = clauses;
	if (only && others.length === 0) return only;
	return exclude ? { _and: clauses } : { _or: clauses };
}

function buildCompositeKeyMatch(key: CompositePrimaryKey, columns: RelatedItemKeyColumn[]): FieldFilter {
	return Object.fromEntries(columns.map(({ key: keyName, column }) => [column, { _eq: key[keyName] }]));
}
